<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <smart-view-dialogue-base
        :close="
            () => {
                this.$emit('close-dialog');
            }
        "
    >
        <template v-slot:heading>
            Downloads for {{ datasetGroupName }}
        </template>
        <template v-slot:content>
            <div class="downloads-dialog">
                <v-alert v-if="downloadErrors.error" type="error" :theme="dialogTheme">
                    There was an error updating the list of downloads.
                </v-alert>
                <v-alert
                    v-if="downloadErrors.forbidden"
                    type="warning"
                    :theme="dialogTheme"
                >
                    You do not have access to the downloads for this dataset.
                </v-alert>
                <v-alert
                    v-if="downloadErrors.processRequestError"
                    type="warning"
                    :theme="dialogTheme"
                >
                    There was an error sending your download request, please try
                    again. If the error persists please contact support.
                </v-alert>
                <v-alert v-if="waiting.length > 0" type="info" :theme="dialogTheme">
                    Your downloads are processing, keep this dialog open to
                    download them once they are ready. Or you can come back
                    later and download.
                </v-alert>
            </div>

            <div
                v-if="!datasetDownloads.length > 0"
                class="downloads-dialog__loading"
            >
                <v-progress-circular
                    indeterminate
                    color="primary"
                ></v-progress-circular>
            </div>

            <div class="downloads-dialog__toolbar">
                <v-select
                    v-if="times.length > 0 && datasetDownloads.length > 0"
                    v-model="selectedTime"
                    :items="times"
                    no-data-text="No times available."
                    :item-value="'uuid'"
                    label="Date"
                    :item-title="'dateTime'"
                    class="downloads-dialog__select"
                    variant="outlined"
                    :theme="dialogTheme"
                >
                </v-select>
            </div>
            <template v-if="datasetDownloads.length > 0">
                <v-row class="downloads-dialog__grid">
                    <v-col
                        v-for="(download, i) in datasetDownloads"
                        :key="i"
                        cols="12"
                        sm="6"
                        md="4"
                    >
                        <v-card class="downloads-dialog__card" flat :theme="dialogTheme">
                            <v-card-title class="downloads-dialog__card-title">
                                {{ getViewOption(download.type).text }}
                            </v-card-title>
                            <v-card-text class="downloads-dialog__card-text">
                                <template v-if="download.state !== 'failed'">
                                    {{
                                        getViewOption(download.type)
                                            .downloadDescription
                                    }}
                                    <span v-if="download.message">
                                        {{ download.message }}
                                    </span>
                                </template>
                                <template v-else>
                                    {{ download.message || 'Error with the download.' }}
                                </template>
                            </v-card-text>
                            <v-card-actions class="downloads-dialog__card-actions">
                                <v-chip
                                    size="small"
                                    variant="tonal"
                                    class="downloads-dialog__state"
                                    :theme="dialogTheme"
                                >
                                    {{ formatState(download.state) }}
                                </v-chip>
                                <div class="flex-grow-1"></div>
                                <v-btn
                                    color="primary"
                                    :theme="dialogTheme"
                                    :disabled="isDownloadDisabled(download)"
                                    :loading="
                                        download.state === 'preparing'
                                    "
                                    @click="
                                        () => {
                                            downloadButtonClick(download);
                                        }
                                    "
                                >
                                    {{ getDownloadActionText(download) }}
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>
            </template>
        </template>
    </smart-view-dialogue-base>
</template>

<script>
import axios from 'axios';
import SmartViewDialogueBase from 'components/View/SmartViewDialogueBase';
import viewsInfoMixin from 'js/mixins/viewsInfoMixin';
import { findIndex } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useDownloadsStore } from 'store/pinia/useDownloadsStore';
import {
    formatDownloadState,
    getDownloadActionText,
    isDownloadDisabled,
} from './downloadState';

const _ = {
    findIndex,
};

export default {
    name: 'Downloads',
    components: {
        SmartViewDialogueBase,
    },
    mixins: [viewsInfoMixin],
    props: {
        view: {
            type: Object,
            default: () => {
                return {
                    datasetPackage: {
                        dataset_name: 'Dataset Name',
                        dataset_group_uuid:
                            '11111111-1111-1111-1111-1111111111',
                    },
                };
            },
        },
    },
    data: () => {
        return {
            selectedTime: null,
            downloadErrors: {
                error: false,
                forbidden: false,
                processRequestError: false,
            },
            waiting: [],
            pollInterval: null,
            requestController: null,
            requestInFlight: false,
        };
    },
    computed: {
        ...mapState(useDownloadsStore, ['datasetGroups', 'datasetTimes']),
        dialogTheme() {
            return this.$vuetify.theme.dark ? 'dark' : 'light';
        },
        datasetGroupUuid() {
            return this.view.datasetPackage.dataset_group_uuid;
        },
        datasetGroupName() {
            return this.view.datasetPackage.name;
        },
        times() {
            return typeof this.datasetTimes[this.datasetGroupUuid] ===
                'undefined'
                ? []
                : this.datasetTimes[this.datasetGroupUuid];
        },
        datasetGroupDownloads() {
            return this.datasetGroups[this.datasetGroupUuid];
        },
        datasetDownloads() {
            if (!this.selectedTime) return [];
            return this.datasetGroupDownloads?.[this.selectedTime] || [];
        },
    },
    watch: {
        datasetGroupUuid: {
            immediate: true,
            handler() {
                if (this.requestController) {
                    this.requestController.abort();
                    this.requestController = null;
                    this.requestInFlight = false;
                }
                this.selectedTime = null;
                this.getDownloads();
            },
        },
    },
    mounted() {
        this.pollInterval = setInterval(() => {
            this.getDownloads();
        }, 5000); // update every 5 seconds
    },
    beforeDestroy() {
        this.teardownPolling();
    },
    beforeUnmount() {
        this.teardownPolling();
    },
    methods: {
        ...mapActions(useDownloadsStore, ['addDownloads', 'addTimes']),
        teardownPolling() {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
            if (this.requestController) {
                this.requestController.abort();
                this.requestController = null;
            }
            this.requestInFlight = false;
        },
        formatState(state) {
            return formatDownloadState(state);
        },
        getViewOption(type) {
            return this.view_options[type] || {
                text: type,
                downloadDescription: 'Download artifact.',
            };
        },
        isDownloadDisabled(download) {
            return isDownloadDisabled(download);
        },
        getDownloadActionText(download) {
            return getDownloadActionText(download);
        },
        /**
         * Updates the downloads for the current dataset group.
         *
         * @author Conor Brosnan <c.brosnan@nationaldrones.com>
         */
        getDownloads() {
            if (!this.datasetGroupUuid || this.requestInFlight) {
                return;
            }

            if (this.requestController) {
                this.requestController.abort();
            }

            this.requestController = new AbortController();
            this.requestInFlight = true;

            axios
                .get(`/api/view/get_downloads/${this.datasetGroupUuid}/`, {
                    signal: this.requestController.signal,
                })
                .then(response => response.data)
                .then(data => {
                    this.addTimes([this.datasetGroupUuid, data.datasets]);
                    this.addDownloads([this.datasetGroupUuid, data.downloads]);

                    this.setSelectedTime();
                    this.checkWaitingDownloads();

                    // If there was a process request error, leave that up until the user tries another process request
                    this.downloadErrors.error = false;
                    this.downloadErrors.forbidden = false;
                })
                .catch(error => {
                    if (
                        error.name === 'CanceledError' ||
                        error.code === 'ERR_CANCELED'
                    ) {
                        return;
                    }

                    if (error.response && error.response.status === 403) {
                        this.downloadErrors.forbidden = true;
                    } else {
                        this.downloadErrors.error = true;
                    }
                })
                .finally(() => {
                    this.requestInFlight = false;
                    this.requestController = null;
                });
        },
        /**
         * Checks whether any of the waiting downloads are ready or have errored
         * and updates the waiting array
         *
         * @author Conor Brosnan <c.brosnan@nationaldrones.com>
         */
        checkWaitingDownloads() {
            for (let i = 0; i < this.waiting.length; i++) {
                const downloads =
                    this.datasetGroupDownloads?.[this.waiting[i].datasetUuid] ||
                    [];
                const index = _.findIndex(
                    downloads,
                    {
                        viewUuid: this.waiting[i].viewUuid,
                    }
                );

                const download = downloads[index];
                if (!download) {
                    continue;
                }

                if (download.state === 'ready') {
                    this.triggerDownload(download);
                    this.waiting.splice(i);
                } else if (download.state === 'error') {
                    this.waiting.splice(i);
                }
            }
        },
        triggerDownload({ url, fileName }) {
            const link = document.createElement('a');
            link.download = fileName;
            link.href = url;
            link.click();
        },
        /**
         * If the dialog was just opened or the dataset group uuid changed,
         * the selected time will be null. Is set to the currently open view
         *
         * @author Conor Brosnan <c.brosnan@nationaldrones.com>
         */
        setSelectedTime() {
            if (this.selectedTime === null) {
                this.$nextTick(() => {
                    const index = _.findIndex(this.times, {
                        uuid: this.view.datasetPackage.uuid,
                    });
                    this.selectedTime = this.times[index].uuid;
                });
            }
        },
        /**
         * Adds a download to the waiting list so the download can be triggered
         * once it is ready.
         *
         * @param {string} viewUuid the download's view uuid
         * @param {string} datasetUuid the download's dataset uuid
         * @author Conor Brosnan <c.brosnan@nationaldrones.com>
         */
        addToWaiting(viewUuid, datasetUuid) {
            this.waiting.push({ viewUuid, datasetUuid });
        },
        sendProcessRequest({ url, viewUuid }) {
            this.downloadErrors.processRequestError = false;
            axios
                .post(url)
                .then(() => {
                    this.getDownloads();
                    this.downloadErrors.processRequestError = false;
                    this.addToWaiting(viewUuid, this.selectedTime);
                })
                .catch(error => {
                    this.downloadErrors.processRequestError = true;
                    throw error;
                });
        },
        /**
         * Handles the download button click on a download
         *
         * @param {object}download the download the button was clicked on
         * @author: Williams Francis <w.francis@nationaldrones.com> | <olawalewilliams9438@gmail.com>
         */
        downloadButtonClick(download) {
            if (this.isDownloadDisabled(download)) {
                return;
            }

            if (download.processRequest) {
                download.state = 'preparing';
                this.sendProcessRequest(download);
            } else {
                var s3_file_path = download.s3_file_path
                var file_name = download.fileName    
                axios.post(
                    'api/view/generate_pre_signed_download_s3_file_url/',
                    JSON.stringify({
                        viewUuid: download.viewUuid,
                        s3_file_path: s3_file_path,
                        file_name: file_name
                    })
                ).then((response) => {
                    // Replace cloudfront url wuth signed generated file downoad_url
                    download.url = response.data.download_url
                    // console.log(response.data.download_url);
                    // console.log(download.url)
                    this.triggerDownload(download);
                })
                .catch(error => {
                    this.downloadErrors.processRequestError = true;
                    console.error('Download URL request failed', error);
                });
            }
        },
    },
};
</script>

<style scoped>
.downloads-dialog__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 180px;
}

.downloads-dialog__toolbar {
    display: flex;
    justify-content: center;
    margin-bottom: 8px;
}

.downloads-dialog__select {
    max-width: 360px;
    width: 100%;
}

.downloads-dialog__grid {
    margin-top: 8px;
}

.downloads-dialog__card {
    height: 100%;
    border: 1px solid var(--sd-border);
    background: var(--sd-surface);
    box-shadow: var(--sd-shadow-soft);
}

.downloads-dialog__card-title {
    color: var(--sd-text);
}

.downloads-dialog__card-text {
    color: var(--sd-text-muted) !important;
    min-height: 96px;
}

.downloads-dialog__card-actions {
    padding: 0 16px 16px;
}

.downloads-dialog__state {
    color: var(--sd-text);
    border: 1px solid var(--sd-border);
}
</style>
